import { forwardRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { uiToolkit } from '/store/toolkits';

import { ISidebarProps, SidebarSide } from './types';
import { useAutomaticToggling, useDefaultToggler, useTogglingOnHover } from './hooks';
import { sidebarClasses, StyledDrawer } from './styles';

export const Sidebar = forwardRef<HTMLDivElement, ISidebarProps>((props, ref) => {
  const {
    side,
    isOpenInitiallyByDefault = false,
    manualTogglingParams,
    shouldBeOpenOnHover = false,
    shouldShowLifeSignOnDefaultTogglerHover = false,
    onTogglingStatusChange,
    children,
    classes: _,
    ...drawerProps
  }: ISidebarProps = props;

  const isNavSidebarPermanentlyOpen: boolean = useSelector(uiToolkit.selectors.navSidebarOpen);
  const navSidebarSide: SidebarSide = useSelector(uiToolkit.selectors.navSidebarSide);

  const { isOpenedManually, openManually, closeManually } = manualTogglingParams || {};

  const {
    defaultToggler,
    isOpenedByDefaultToggler,
    isDefaultTogglerHovered,
    toggleIsOpenedByDefaultTogglerOn,
    toggleIsOpenedByDefaultTogglerOff,
  } = useDefaultToggler(isOpenInitiallyByDefault);

  const isPermanentlyOpen: boolean = isOpenedManually !== undefined ? isOpenedManually : isOpenedByDefaultToggler;

  const { PaperProps, isOpenedByHovering } = useTogglingOnHover(isPermanentlyOpen, shouldBeOpenOnHover);

  const isOpen = useMemo<boolean>(() => {
    if (isOpenedByHovering) {
      return true;
    }

    return isPermanentlyOpen;
  }, [isOpenedByHovering, isPermanentlyOpen]);

  useAutomaticToggling({
    side,
    isSidebarOpen: isOpen,
    openSidebar: openManually || toggleIsOpenedByDefaultTogglerOn,
    closeSidebar: closeManually || toggleIsOpenedByDefaultTogglerOff,
  });

  useEffect(() => {
    if (!onTogglingStatusChange) {
      return;
    }

    onTogglingStatusChange(isOpen);
  }, [onTogglingStatusChange, isOpen]);

  return (
    <StyledDrawer
      ref={ref}
      classes={{
        root: `
          ${isOpen ? sidebarClasses.sidebarOpen : ''}
          ${
            shouldShowLifeSignOnDefaultTogglerHover && isDefaultTogglerHovered
              ? sidebarClasses.pseudoToggleAnimating
              : ''
          }
        `,
        paper: sidebarClasses.drawerPaper,
      }}
      open={isOpen}
      PaperProps={{ ...PaperProps }}
      anchor={side}
      $isToggledManually={Boolean(manualTogglingParams)}
      $navSidebarSide={navSidebarSide}
      $isNavSidebarPermanentlyOpen={isNavSidebarPermanentlyOpen}
      {...drawerProps}
      onOpen={openManually || toggleIsOpenedByDefaultTogglerOn}
      onClose={closeManually || toggleIsOpenedByDefaultTogglerOff}
    >
      {!manualTogglingParams ? defaultToggler : null}

      {children}
    </StyledDrawer>
  );
});
